import React, { useState, useEffect } from 'react';
import { issuesAPI } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';

function IssuesManagement({ onBack }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSourceType, setFilterSourceType] = useState('');
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [priority, setPriority] = useState('medium');
  const [updating, setUpdating] = useState(false);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    fetchIssues();
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterSourceType]);

  // Auto-expand issue if passed from dashboard
  useEffect(() => {
    if (location.state?.selectedIssueId && issues.length > 0) {
      const issue = issues.find(i => i._id === location.state.selectedIssueId);
      if (issue) {
        setExpandedIssue(issue._id);
        setPriority(issue.priority || 'medium');
        setAdminNotes(issue.adminNotes || '');
        // Scroll to the issue
        setTimeout(() => {
          const element = document.getElementById(`issue-${issue._id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [location.state, issues]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterSourceType) params.sourceType = filterSourceType;

      const response = await issuesAPI.getAllIssues(params);
      setIssues(response.data.issues || []);
    } catch (error) {
      console.error('Error fetching issues:', error);
      alert('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await issuesAPI.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleUpdateIssue = async (issueId, status) => {
    setUpdating(true);
    try {
      await issuesAPI.updateIssue(issueId, {
        status,
        adminNotes,
        priority
      });
      alert('Issue updated successfully');
      setExpandedIssue(null);
      setAdminNotes('');
      fetchIssues();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating issue:', error);
      alert('Failed to update issue');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Are you sure you want to delete this issue report?')) {
      return;
    }

    try {
      await issuesAPI.deleteIssue(issueId);
      alert('Issue deleted successfully');
      fetchIssues();
      fetchStatistics();
    } catch (error) {
      console.error('Error deleting issue:', error);
      alert('Failed to delete issue');
    }
  };

  const navigateToQuestion = (issue) => {
    const { sourceType, sourceId, metadata } = issue;

    // Build navigation URL based on source type
    switch (sourceType) {
      case 'quiz':
        // Navigate to admin quiz details page
        navigate(`/admin/quiz/${sourceId}`);
        break;
      case 'assetQuiz':
        if (metadata?.courseId) {
          // Navigate to course content where asset quiz is located
          navigate(`/course/${metadata.courseId}/content`);
        } else {
          alert('Course information not available for this asset quiz');
        }
        break;
      case 'assignment':
        // Navigate to assignment details page
        navigate(`/assignment-details/${sourceId}`);
        break;
      case 'aiQuestion':
        if (metadata?.courseId) {
          // Navigate to course where AI questions are
          navigate(`/course/${metadata.courseId}/content`);
        } else {
          alert('Course information not available for this AI question');
        }
        break;
      case 'flashcard':
        // Navigate to flashcard management
        navigate(`/admin/flashcards`);
        break;
      default:
        alert('Unable to navigate to this question type');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#92400e', text: 'Pending' },
      investigating: { bg: '#dbeafe', color: '#1e3a8a', text: 'Investigating' },
      resolved: { bg: '#d1fae5', color: '#065f46', text: 'Resolved' },
      dismissed: { bg: '#e5e7eb', color: '#374151', text: 'Dismissed' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: { bg: '#e5e7eb', color: '#374151', text: 'Low' },
      medium: { bg: '#fef3c7', color: '#92400e', text: 'Medium' },
      high: { bg: '#fed7d7', color: '#c53030', text: 'High' }
    };
    const style = styles[priority] || styles.medium;
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '8px',
        fontSize: '11px',
        fontWeight: '600',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.text}
      </span>
    );
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      wrong_answer: 'Wrong Answer',
      ambiguous_question: 'Ambiguous Question',
      typo: 'Typo/Grammar',
      missing_option: 'Missing Option',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const getSourceTypeLabel = (type) => {
    const labels = {
      quiz: 'Quiz',
      assetQuiz: 'Course Quiz',
      assignment: 'Assignment',
      aiQuestion: 'AI Question',
      flashcard: 'Flashcard'
    };
    return labels[type] || type;
  };

  // Skeleton Loader Component
  const SkeletonLoader = () => (
    <div className="container">
      <div className="card" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header Skeleton */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            width: '120px',
            height: '36px',
            backgroundColor: '#e2e8f0',
            borderRadius: '6px',
            marginBottom: '16px',
            animation: 'pulse 1.5s ease-in-out infinite'
          }} />

          <div style={{ marginBottom: '20px' }}>
            <div style={{
              width: '280px',
              height: '32px',
              backgroundColor: '#e2e8f0',
              borderRadius: '8px',
              marginBottom: '8px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }} />
            <div style={{
              width: '380px',
              height: '20px',
              backgroundColor: '#e2e8f0',
              borderRadius: '6px',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: '0.2s'
            }} />
          </div>

          {/* Statistics Skeleton */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{
                padding: '16px',
                backgroundColor: '#f7fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  width: '60px',
                  height: '36px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.1}s`
                }} />
                <div style={{
                  width: '100px',
                  height: '16px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.1 + 0.1}s`
                }} />
              </div>
            ))}
          </div>

          {/* Filters Skeleton */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
            {[1, 2].map((i) => (
              <div key={i}>
                <div style={{
                  width: '60px',
                  height: '16px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '4px',
                  marginBottom: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`
                }} />
                <div style={{
                  width: '180px',
                  height: '40px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '6px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.15 + 0.1}s`
                }} />
              </div>
            ))}
          </div>
        </div>

        {/* Issues List Skeleton */}
        <div style={{ display: 'grid', gap: '16px' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              padding: '20px',
              backgroundColor: '#fff'
            }}>
              {/* Issue Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    width: '120px',
                    height: '24px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '12px',
                    marginBottom: '8px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${i * 0.2}s`
                  }} />
                  <div style={{
                    width: '200px',
                    height: '20px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '6px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${i * 0.2 + 0.1}s`
                  }} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{
                    width: '80px',
                    height: '28px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '12px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${i * 0.2 + 0.2}s`
                  }} />
                  <div style={{
                    width: '60px',
                    height: '28px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '8px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${i * 0.2 + 0.3}s`
                  }} />
                </div>
              </div>

              {/* Issue Content */}
              <div style={{
                width: '100%',
                height: '16px',
                backgroundColor: '#e2e8f0',
                borderRadius: '4px',
                marginBottom: '8px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${i * 0.2 + 0.4}s`
              }} />
              <div style={{
                width: '85%',
                height: '16px',
                backgroundColor: '#e2e8f0',
                borderRadius: '4px',
                marginBottom: '8px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${i * 0.2 + 0.5}s`
              }} />
              <div style={{
                width: '60%',
                height: '16px',
                backgroundColor: '#e2e8f0',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${i * 0.2 + 0.6}s`
              }} />
            </div>
          ))}
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={onBack}
            style={{ marginBottom: '16px', fontSize: '12px' }}
          >
            ← Back to Dashboard
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '24px', marginBottom: '4px' }}>
                🚩 Question Issues Tracker
              </h2>
              <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
                Review and manage reported issues with questions across the platform
              </p>
            </div>
          </div>

          {/* Statistics */}
          {statistics && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f7fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#667eea' }}>
                  {statistics.total}
                </div>
                <div style={{ fontSize: '13px', color: '#718096', marginTop: '4px' }}>
                  Total Issues
                </div>
              </div>
              {Object.entries(statistics.byStatus || {}).map(([status, count]) => (
                <div key={status} style={{
                  padding: '16px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748' }}>
                    {count}
                  </div>
                  <div style={{ fontSize: '13px', color: '#718096', marginTop: '4px' }}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>
                Status:
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px'
                }}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '4px' }}>
                Source:
              </label>
              <select
                value={filterSourceType}
                onChange={(e) => setFilterSourceType(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px'
                }}
              >
                <option value="">All Sources</option>
                <option value="quiz">Quiz</option>
                <option value="assetQuiz">Course Quiz</option>
                <option value="assignment">Assignment</option>
                <option value="aiQuestion">AI Question</option>
                <option value="flashcard">Flashcard</option>
              </select>
            </div>
          </div>
        </div>

        {/* Issues List */}
        {issues.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            color: '#718096',
            backgroundColor: '#f7fafc',
            borderRadius: '12px',
            border: '2px dashed #cbd5e0'
          }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>🎉 No issues found</p>
            <p style={{ fontSize: '13px' }}>
              {filterStatus || filterSourceType 
                ? 'Try adjusting your filters' 
                : 'No issues have been reported yet'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {issues.map((issue) => (
              <div
                key={issue._id}
                id={`issue-${issue._id}`}
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#fff'
                }}
              >
                {/* Issue Header */}
                <div
                  style={{
                    padding: '16px 20px',
                    backgroundColor: '#f7fafc',
                    borderBottom: '1px solid #e2e8f0',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedIssue(expandedIssue === issue._id ? null : issue._id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        {getStatusBadge(issue.status)}
                        {getPriorityBadge(issue.priority)}
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: '#e2e8f0',
                          color: '#4a5568'
                        }}>
                          {getSourceTypeLabel(issue.sourceType)}
                        </span>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: '#fef3c7',
                          color: '#92400e'
                        }}>
                          {getIssueTypeLabel(issue.issueType)}
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#2d3748', marginBottom: '6px' }}>
                        <strong>Source:</strong> {issue.metadata?.sourceName || 'N/A'}
                        {issue.metadata?.sourceCode && ` (${issue.metadata.sourceCode})`}
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096' }}>
                        Reported by <strong>{issue.reportedBy?.name || 'Unknown'}</strong> • 
                        {' '}{new Date(issue.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToQuestion(issue);
                      }}
                      style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#fff',
                        backgroundColor: '#667eea',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginLeft: '12px'
                      }}
                    >
                      📍 Go to Question
                    </button>
                  </div>
                </div>

                {/* Issue Details (Expanded) */}
                {expandedIssue === issue._id && (
                  <div style={{ padding: '20px' }}>
                    {/* Reporter's Description */}
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2d3748',
                        marginBottom: '8px'
                      }}>
                        Issue Description:
                      </h4>
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#f7fafc',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#4a5568',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {issue.description}
                      </div>
                    </div>

                    {/* Attachments */}
                    {issue.attachments && issue.attachments.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2d3748',
                          marginBottom: '8px'
                        }}>
                          Attachments:
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                          {issue.attachments.map((attachment, idx) => (
                            <div key={idx} style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              backgroundColor: '#fff'
                            }}>
                              {attachment.type === 'image' ? (
                                <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                                  <img 
                                    src={attachment.url} 
                                    alt={`Attachment ${idx + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '150px',
                                      objectFit: 'cover',
                                      cursor: 'pointer'
                                    }}
                                  />
                                </a>
                              ) : (
                                <video 
                                  controls
                                  style={{
                                    width: '100%',
                                    height: '150px',
                                    backgroundColor: '#000'
                                  }}
                                >
                                  <source src={attachment.url} />
                                  Your browser does not support the video tag.
                                </video>
                              )}
                              <div style={{
                                padding: '8px',
                                fontSize: '12px',
                                color: '#718096',
                                textAlign: 'center',
                                borderTop: '1px solid #e2e8f0'
                              }}>
                                {attachment.type === 'image' ? '🖼️ Image' : '🎥 Video'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Question Snapshot */}
                    {issue.questionSnapshot && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2d3748',
                          marginBottom: '8px'
                        }}>
                          Question Snapshot:
                        </h4>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fffbeb',
                          borderRadius: '8px',
                          border: '1px solid #fbbf24'
                        }}>
                          <div style={{ fontSize: '14px', color: '#78350f', marginBottom: '8px' }}>
                            {issue.questionSnapshot.question}
                          </div>
                          {issue.questionSnapshot.options && issue.questionSnapshot.options.length > 0 && (
                            <div>
                              {issue.questionSnapshot.options.map((option, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    fontSize: '13px',
                                    color: '#92400e',
                                    padding: '4px 8px',
                                    backgroundColor: idx === issue.questionSnapshot.correctAnswer ? '#bef264' : '#fff',
                                    borderRadius: '4px',
                                    marginTop: '4px'
                                  }}
                                >
                                  {idx + 1}. {option}
                                  {idx === issue.questionSnapshot.correctAnswer && ' ✓'}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Actions */}
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#f0f4ff',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e0'
                    }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2d3748',
                        marginBottom: '12px'
                      }}>
                        Admin Actions:
                      </h4>

                      {/* Priority */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '13px', color: '#4a5568', display: 'block', marginBottom: '4px' }}>
                          Priority:
                        </label>
                        <select
                          value={priority}
                          onChange={(e) => setPriority(e.target.value)}
                          style={{
                            padding: '8px',
                            fontSize: '13px',
                            border: '1px solid #cbd5e0',
                            borderRadius: '6px',
                            width: '150px'
                          }}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>

                      {/* Admin Notes */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '13px', color: '#4a5568', display: 'block', marginBottom: '4px' }}>
                          Admin Notes:
                        </label>
                        <textarea
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Add notes about this issue..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: '13px',
                            border: '1px solid #cbd5e0',
                            borderRadius: '6px',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      {/* Existing Admin Notes */}
                      {issue.adminNotes && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '13px', color: '#4a5568', marginBottom: '4px' }}>
                            Previous Notes:
                          </div>
                          <div style={{
                            padding: '8px',
                            backgroundColor: '#fff',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#2d3748',
                            border: '1px solid #e2e8f0'
                          }}>
                            {issue.adminNotes}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                        <button
                          onClick={() => navigateToQuestion(issue)}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#667eea',
                            backgroundColor: '#fff',
                            border: '2px solid #667eea',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#667eea';
                            e.target.style.color = '#fff';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.color = '#667eea';
                          }}
                        >
                          📝 Go to Question
                        </button>
                      </div>

                      {/* Status Update Buttons */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleUpdateIssue(issue._id, 'investigating')}
                          disabled={updating}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#fff',
                            backgroundColor: '#3b82f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: updating ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Mark as Investigating
                        </button>
                        <button
                          onClick={() => handleUpdateIssue(issue._id, 'resolved')}
                          disabled={updating}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#fff',
                            backgroundColor: '#10b981',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: updating ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Mark as Resolved
                        </button>
                        <button
                          onClick={() => handleUpdateIssue(issue._id, 'dismissed')}
                          disabled={updating}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#fff',
                            backgroundColor: '#6b7280',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: updating ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Dismiss
                        </button>
                        <button
                          onClick={() => handleDeleteIssue(issue._id)}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#fff',
                            backgroundColor: '#ef4444',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginLeft: 'auto'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default IssuesManagement;
